import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mail, MapPin, Phone, Cpu, Network, BrainCircuit } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const formRef = useRef(null);
  
  // Animation particles
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    // Initialize particles
    const particleCount = 30;
    const newParticles = [];
    
    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * 800,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() - 0.5) * 0.3,
        speedY: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.5 + 0.1
      });
    }
    
    setParticles(newParticles);
    
    // Particle animation loop
    const interval = setInterval(() => {
      setParticles(prevParticles => 
        prevParticles.map(p => ({
          ...p,
          x: (p.x + p.speedX + window.innerWidth) % window.innerWidth,
          y: (p.y + p.speedY + 800) % 800
        }))
      );
    }, 50);
    
    return () => clearInterval(interval);
  }, []);

  // Validate environment variables on component mount
  useEffect(() => {
    const requiredEnvVars = [
      'REACT_APP_EMAILJS_SERVICE_ID',
      'REACT_APP_EMAILJS_TEMPLATE_ID',
      'REACT_APP_EMAILJS_PUBLIC_KEY'
    ];

    const missingEnvVars = requiredEnvVars.filter(
      (envVar) => !process.env[envVar]
    );

    if (missingEnvVars.length > 0) {
      console.error('Missing environment variables:', missingEnvVars);
      setStatus({
        type: 'error',
        message: 'Contact form is not properly configured. Please try again later.'
      });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if environment variables are available
    if (!process.env.REACT_APP_EMAILJS_SERVICE_ID || 
        !process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 
        !process.env.REACT_APP_EMAILJS_PUBLIC_KEY) {
      setStatus({
        type: 'error',
        message: 'Contact form is not properly configured. Please try again later.'
      });
      return;
    }

    setStatus({ type: 'loading', message: 'Sending message...' });

    try {
      const templateParams = {
        from_name: formData.name,
        message: formData.message,
        to_name: 'Arjun',
        reply_to: formData.email,
      };

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setStatus({
        type: 'success',
        message: 'Thank you! Your message has been sent successfully.'
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      setStatus({
        type: 'error',
        message: 'Oops! Something went wrong. Please try again later.'
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-black relative overflow-hidden">
      {/* Neural network background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:20px_20px] opacity-20" />
      
      {/* Animated particles */}
      {particles.map((particle, index) => (
        <motion.div
          key={index}
          className="absolute rounded-full bg-white"
          style={{
            x: particle.x,
            y: particle.y,
            width: particle.size,
            height: particle.size,
            opacity: particle.opacity,
          }}
          animate={{
            opacity: [particle.opacity, particle.opacity * 1.5, particle.opacity],
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto px-4 relative z-10"
      >
        <div className="text-center mb-16">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-block"
          >
            <h2 className="text-5xl md:text-6xl font-bold relative">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Get In Touch
              </span>
              <motion.div
                className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-white to-gray-400 rounded-full"
                initial={{ width: "0%" }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              />
            </h2>
          </motion.div>
          
          {/* AI/ML themed decorative elements */}
          <motion.div 
            className="flex justify-center gap-16 mt-4 text-gray-600 opacity-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 0.2 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Network className="w-8 h-8" />
            <Cpu className="w-8 h-8" />
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div 
            className="space-y-8"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="space-y-6">
              <h3 className="text-3xl font-semibold text-white flex items-center gap-2">
                <BrainCircuit className="w-8 h-8 text-gray-500" />
                <span>Let's Collaborate</span>
              </h3>
              <p className="text-gray-400 leading-relaxed">
                I'm always open to discussing AI projects, creative ideas, or opportunities to build innovative solutions that push the boundaries of technology.
              </p>
            </div>

            <AnimatePresence mode="wait">
              <motion.div layout className="space-y-6">
                <ContactInfo
                  icon={<Mail className="w-6 h-6" />}
                  title="Email"
                  content="arjunshukla6558@gmail.com"
                  link="mailto:arjunshukla6558@gmail.com"
                />
                <ContactInfo
                  icon={<MapPin className="w-6 h-6" />}
                  title="Location"
                  content="Pune, India"
                />
                <ContactInfo
                  icon={<Phone className="w-6 h-6" />}
                  title="Phone"
                  content="+91 88304 45632"
                />
              </motion.div>
            </AnimatePresence>
            
            {/* Additional element with glow effect */}
            <motion.div
              className="relative p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className="absolute inset-0 opacity-30">
                <Network className="absolute top-4 right-4 w-24 h-24 text-white opacity-10" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-2">AI Consultation</h4>
              <p className="text-gray-400">
                Need advice on implementing AI in your project? Schedule a consultation call to discuss your specific needs and potential solutions.
              </p>
              
              <motion.div
                className="absolute -inset-1 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                initial={{ left: "-100%" }}
                animate={{ left: "100%" }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "loop",
                  ease: "easeInOut",
                  repeatDelay: 1
                }}
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <motion.div 
              className="absolute -inset-1 rounded-xl bg-gradient-to-r from-white/5 to-gray-500/5 blur-xl"
              animate={{
                opacity: [0.2, 0.4, 0.2],
                scale: [0.98, 1.01, 0.98]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            
            <motion.form
              ref={formRef}
              onSubmit={handleSubmit}
              className="space-y-6 bg-white/5 p-6 rounded-xl shadow-lg border border-white/10 backdrop-blur-sm relative z-10"
            >
              <InputField
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                icon={<span className="text-gray-500 text-sm">01</span>}
              />
              <InputField
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                icon={<span className="text-gray-500 text-sm">02</span>}
              />
              <div className="relative group">
                <div className="absolute top-4 left-4 text-gray-500 text-sm">03</div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your Message"
                  rows="6"
                  className="w-full px-4 py-3 pl-10 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-white/20 transition-all duration-300 text-white resize-none group-hover:bg-white/10"
                  required
                />
              </div>

              <AnimatePresence mode="wait">
                {status.message && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className={`p-3 rounded-lg ${
                      status.type === 'success' ? 'bg-white/10 text-green-400' :
                      status.type === 'error' ? 'bg-white/10 text-red-400' :
                      'bg-white/10 text-gray-400'
                    }`}
                  >
                    {status.message}
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02, backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
                whileTap={{ scale: 0.98 }}
                disabled={status.type === 'loading'}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 border border-white/10 rounded-lg text-white font-medium transition-all duration-300 hover:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden relative group"
              >
                <motion.span className="relative z-10 flex items-center gap-2">
                  {status.type === 'loading' ? 'Processing...' : 'Send Message'}
                  <Send className="w-4 h-4" />
                </motion.span>
                
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.6 }}
                />
              </motion.button>
            </motion.form>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

const InputField = ({ type, name, value, onChange, placeholder, icon }) => (
  <div className="relative group">
    <div className="absolute top-1/2 left-4 -translate-y-1/2 text-gray-500 z-10">
      {icon}
    </div>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-4 py-3 pl-10 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-white/20 transition-all duration-300 text-white group-hover:bg-white/10"
      required
    />
    <motion.div
      className="absolute bottom-0 left-0 h-px w-0 bg-gradient-to-r from-white/50 to-gray-500/50"
      initial={{ width: 0 }}
      whileHover={{ width: '100%' }}
      transition={{ duration: 0.3 }}
    />
  </div>
);

const ContactInfo = ({ icon, title, content, link }) => (
  <motion.div
    className="flex items-start gap-4 group"
    initial={{ x: 0 }}
    whileHover={{ x: 5 }}
  >
    <motion.div 
      className="p-3 bg-white/5 rounded-lg text-gray-400 relative overflow-hidden"
      whileHover={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        scale: 1.05
      }}
    >
      <motion.div className="relative z-10">
        {icon}
      </motion.div>
      <motion.div 
        className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0"
        initial={{ x: "-100%" }}
        whileHover={{ x: "100%" }}
        transition={{ duration: 0.6 }}
      />
    </motion.div>
    <div>
      <h4 className="text-white font-medium">{title}</h4>
      {link ? (
        <a
          href={link}
          className="text-gray-400 hover:text-white transition-colors"
        >
          {content}
        </a>
      ) : (
        <p className="text-gray-400">{content}</p>
      )}
    </div>
  </motion.div>
);

export default Contact;